import { gql } from '@apollo/client';

export const PROJECT_BASIC_DATA = `
  id
  type
  code
  title
  status
  description
  createdAt
  updatedAt
  keywords
  targetN
  surveyLink
  schedulingSettings {
    attendanceRequirement
    leadHours
    bufferMinutes
    availableDays
    startDate
    endDate
  }
  preInterviewLink
  owners {
    id
    email
    timeZone
  }
  qualtricsSurvey {
    surveyId
    status
    qualtricsUsers {
      username
      password
    }
  }
  qualificationQuestions {
    id
    question
    questionType
    answerChoices
    otherIncluded
  }
  numUpcomingMeetings
  numPendingInvites
  numCompleted
  deletable
  hasTranscript
`;

const PROJECT_EXPERT_RECOMMENDATIONS_PRESENCE_DATA = `
  expertRecommendations {
    generationStatus
    uniqueExpertIds
    evaluations {
      evaluationStatus
    }
    criteria {
      description
    }
  }
`;

const PROJECT_REPORT_PRESENCE_DATA = `
  aiReport {
    generationStatus
    generationError
    sections {
      id
    }
  }
`;

const PROJECT_REPORT_REFERENCE_DATA = `
  id
  jobId
  expertId
  quote
  label
  sourceType
  expert {
    id
    fullName
    title
    country
    organization {
      name
      type
    }
  }
`;

const PROJECT_EXPERT_RECOMMENDATIONS_DATA = `
  expertRecommendations {
    generationStatus
    lastGeneratedAt
    uniqueExpertIds
    criteria {
      description
      expertIds
      total
      searchStatus
    }
    evaluations {
      expertId
      verdict
      evaluationStatus
    }
  }
  jobs {
    id
    overall_status
    meetingAt
    user {
      id
    }
  }
`;

const PROJECT_DETAIL_DATA = `
  ${PROJECT_BASIC_DATA}
  requiresProfessionalRegistration
  ${PROJECT_EXPERT_RECOMMENDATIONS_PRESENCE_DATA}
  ${PROJECT_REPORT_PRESENCE_DATA}
`;

const PROJECT = gql`
  query Project($id: String!) {
    project(id: $id) {
      ${PROJECT_DETAIL_DATA}
    }
  }
`;

export const PROJECT_EXPERT_RECOMMENDATIONS = gql`
  query ProjectExpertRecommendations($id: String!) {
    project(id: $id) {
      id
      ${PROJECT_EXPERT_RECOMMENDATIONS_DATA}
    }
  }
`;

export const PROJECT_EXPERT_EVALUATION_PAGE = gql`
  query ProjectExpertEvaluationPage(
    $id: String!
    $criterion: String
    $includeHidden: Boolean
    $pagination: Pagination
  ) {
    project(id: $id) {
      id
      expertRecommendations {
        evaluationPage(criterion: $criterion, includeHidden: $includeHidden, pagination: $pagination) {
          total
          evaluations {
            expertId
            expertName
            expertTitle
            expertOrganizationName
            evaluation
            verdict
          }
        }
      }
    }
  }
`;

export const PROJECT_REPORT = gql`
  query ProjectReport($id: String!) {
    project(id: $id) {
      id
      aiReport {
        generationStatus
        generationError
        lastGeneratedAt
        sourceFileCount
        modelName
        sections {
          id
          title
          order
          blocks
        }
        references {
          ${PROJECT_REPORT_REFERENCE_DATA}
        }
      }
    }
  }
`;

export const PROJECTS = gql`
  query Projects($query: String, $filters: ProjectFilters!, $pagination: Pagination) {
    projects(query: $query, filters: $filters, pagination: $pagination) {
      total
      projects {
        ${PROJECT_BASIC_DATA}
      }
    }
  }
`;

export const PROJECT_METADATA = gql`
  query ProjectMetadata($id: String!) {
    project(id: $id) {
      id
      type
      title
      description
      keywords
      team {
        id
        name
      }
      owners {
        id
      }
      preInterviewLink
      requiresProfessionalRegistration
      qualtricsSurvey {
        surveyId
      }
    }
  }
`;

export const PROJECT_FORECAST = gql`
  query ProjectForecast($id: String!) {
    project(id: $id) {
      id
      keywords
      jobs {
        id
        meetingAt
        overall_status
        completedAt
        acceptProbabilities
        expert {
          id
          country
          organization {
            type
          }
          technology_tags
          medical_specialties
        }
      }
    }
  }
`;

export const PROJECT_EXPORT = gql`
  query ProjectExport($id: String!) {
    project(id: $id) {
      id
      jobs {
        id
        meetingAt
        overall_status
        completedAt
        qualificationResponses {
          question
          response
        }
        user {
          id
          email
        }
        expert {
          id
          fullName
          title
          country
          organization {
            name
            type
          }
          technology_tags
          medical_specialties
          hourlyRate
        }
      }
    }
  }
`;

export const PROJECT_CALENDAR = gql`
  query Project($id: String!) {
    project(id: $id) {
      id
      calendar {
        date
        entries {
          startTime
          status
        }
      }
    }
  }
`;

export const PROJECT_OWNERS_EXTERNAL_ACCOUNT_STATUSES = gql`
  query Project($id: String!) {
    project(id: $id) {
      id
      owners {
        id
        email
        externalAccountStatuses {
          id
          provider
          status
          tokenExpiresAt
        }
      }
    }
  }
`;

export const CREATE_PROJECT = gql`
  mutation CreateProject($input: ProjectInput!, $file: Upload) {
    createProject(input: $input, file: $file) {
      ${PROJECT_BASIC_DATA}
    }
  }
`;

export const UPDATE_PROJECT = gql`
  mutation UpdateProject($id: ID!, $input: ProjectInput!) {
    updateProject(id: $id, input: $input) {
      ${PROJECT_BASIC_DATA}
      requiresProfessionalRegistration
    }
  }
`;

export const DELETE_PROJECT = gql`
  mutation DeleteProject($id: ID!) {
    deleteProject(id: $id) {
      id
    }
  }
`;

export const COMPLETE_PROJECT = gql`
  mutation completeProject($id: ID!) {
    completeProject(id: $id) {
      id
      status
    }
  }
`;

export const SEND_PROJECT_LANDING_INVITE = gql`
  mutation SendProjectLandingInvite($input: ProjectLandingInviteInput!) {
    sendProjectLandingInvite(input: $input)
  }
`;

export const RECOMMEND_PROJECT_EXPERTS = gql`
  mutation RecommendProjectExperts($projectId: ID!) {
    recommendProjectExperts(projectId: $projectId) {
      id
      expertRecommendations {
        generationStatus
        uniqueExpertIds
        evaluations {
          evaluationStatus
        }
        criteria {
          description
        }
      }
    }
  }
`;

export const REPORT_JOB_TRANSCRIPT = gql`
  query ReportJobTranscript($projectId: ID!, $jobId: ID!) {
    reportJob(projectId: $projectId, jobId: $jobId) {
      id
      zoomMeeting {
        meetingReport {
          recordings {
            contentType
            content
          }
        }
      }
    }
  }
`;

export const GENERATE_PROJECT_REPORT = gql`
  mutation GenerateProjectReport($projectId: ID!, $directive: String) {
    generateProjectReport(projectId: $projectId, directive: $directive) {
      id
      aiReport {
        generationStatus
        generationError
        lastGeneratedAt
        directive
      }
    }
  }
`;

const PROJECT_SEARCH_LIST_DATA = `
  id
  type
  title
  status
  createdAt
  updatedAt
  keywords
  owners {
    id
    email
  }
  qualtricsSurvey(refresh: false) {
    status
  }
  numUpcomingMeetings
  numPendingInvites
  numCompleted
  deletable
`;

export const SEARCH_PROJECTS = gql`
  query SearchProjects($query: String!, $pagination: Pagination) {
    searchProjects(query: $query, pagination: $pagination) {
      total
      items {
        highlights {
          field
          segments
        }
        instanceCount
        item {
          __typename
          ... on Project {
            ${PROJECT_SEARCH_LIST_DATA}
          }
        }
      }
    }
  }
`;

export default PROJECT;
